import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { EmptyState } from '../components/EmptyState';
import { Search, Flame, Plus, X } from 'lucide-react';
import { API } from '@/lib/api';

// Reto de 21 días: flag simple + fecha de inicio sobre el paciente
// (ver PUT /patients/{id}/reto21). No maneja rondas ni grupos.
export const Reto21Page = () => {
  const [activos, setActivos]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [busqueda, setBusqueda]   = useState('');
  const [resultados, setResultados] = useState([]);

  const fetchActivos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/patients`, { params: { en_reto21: true, limit: 500 } });
      setActivos(res.data);
    } catch { toast.error('Error al cargar el reto de 21 días'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchActivos(); }, [fetchActivos]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!busqueda.trim()) { setResultados([]); return; }
      try {
        const res = await axios.get(`${API}/patients`, { params: { search: busqueda, limit: 10 } });
        setResultados(res.data.filter(p => !p.reto_21_activo));
      } catch { /* silencioso: es solo autocompletado */ }
    }, 400);
    return () => clearTimeout(timer);
  }, [busqueda]);

  const agregar = async (patient) => {
    try {
      await axios.put(`${API}/patients/${patient.id}/reto21`, { activo: true });
      toast.success(`${patient.nombre} se unió al reto de 21 días`);
      setBusqueda('');
      setResultados([]);
      fetchActivos();
    } catch { toast.error('Error al agregar al reto'); }
  };

  const quitar = async (patient) => {
    try {
      await axios.put(`${API}/patients/${patient.id}/reto21`, { activo: false });
      setActivos(prev => prev.filter(p => p.id !== patient.id));
      toast.success(`${patient.nombre} salió del reto`);
    } catch { toast.error('Error al quitar del reto'); }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="reto21-page">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Reto de 21 días</h1>
          <p className="text-muted-foreground mt-1">
            Pacientes inscritos en el reto (con la nutrióloga u otros)
          </p>
        </div>

        <Card className="bg-card border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold">Agregar paciente al reto</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar paciente por nombre o teléfono..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="pl-9"
              />
            </div>
            {resultados.length > 0 && (
              <div className="space-y-1">
                {resultados.map((p) => (
                  <div key={p.id} className="flex items-center justify-between p-2 rounded-lg border border-border/50">
                    <span className="text-sm">{p.nombre} {p.apellido} — {p.telefono}</span>
                    <Button size="sm" onClick={() => agregar(p)}>
                      <Plus className="h-3.5 w-3.5 mr-1" /> Agregar
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-card border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold">Activos en el reto ({activos.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-muted-foreground text-center py-6">Cargando...</p>
            ) : activos.length === 0 ? (
              <EmptyState
                icon={Flame}
                title="Nadie está en el reto todavía"
                description="Busca un paciente arriba y agrégalo al reto de 21 días."
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {activos.map((p) => (
                  <div key={p.id} className="p-4 rounded-xl border border-border/50 bg-card flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{p.nombre} {p.apellido}</p>
                      <p className="text-sm text-muted-foreground">{p.telefono}</p>
                      {p.reto_21_fecha_inicio && (
                        <Badge variant="outline" className="mt-2">
                          Desde {p.reto_21_fecha_inicio}
                        </Badge>
                      )}
                    </div>
                    <Button
                      variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
                      onClick={() => quitar(p)} aria-label={`Quitar a ${p.nombre} del reto`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
